import React from 'react';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';

// Button surfaces. Teams and Zoom both reserve red for leaving the call and
// keep a toggled-off control neutral: the slashed glyph carries the state, not
// an alarm colour. "Camera off" previously rendered the same red as "End
// Meeting", which read as an error rather than a toggle.
const RED = 'linear-gradient(145deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%)';
const RED_HOVER = 'linear-gradient(145deg, rgba(239, 68, 68, 0.3) 0%, rgba(220, 38, 38, 0.3) 100%)';
const BLUE = 'linear-gradient(145deg, rgba(96, 165, 250, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)';
const BLUE_HOVER = 'linear-gradient(145deg, rgba(96, 165, 250, 0.25) 0%, rgba(59, 130, 246, 0.25) 100%)';
const OFF = 'rgba(255, 255, 255, 0.10)';
const OFF_HOVER = 'rgba(255, 255, 255, 0.18)';

// recording is only ever passed to the call button, so it is checked first:
// End Meeting stays red even though it is "active".
const surface = (props) => (props.recording ? RED : !props.active ? OFF : BLUE);
const surfaceHover = (props) => (props.recording ? RED_HOVER : !props.active ? OFF_HOVER : BLUE_HOVER);
const ink = (props) => (props.recording ? '#ef4444' : !props.active ? 'rgba(255, 255, 255, 0.92)' : '#60a5fa');

const ToolbarButton = styled.button.withConfig({
  shouldComponentUpdate: true,
  // These props should not be passed to the DOM element
  componentId: 'ToolbarButton',
  shouldComponentUpdate: true,
  // Tell styled-components which props are for styling only
  shouldForwardProp: (prop) => !['recording', 'active', 'hasLabel'].includes(prop),
})`
  display: inline-flex;
  flex-direction: ${props => (props.hasLabel ? 'column' : 'row')};
  align-items: center;
  justify-content: center;
  gap: ${props => (props.hasLabel ? '4px' : '6px')};
  min-width: 36px;
  height: 36px;
  padding: ${props => (props.hasLabel ? '6px 10px' : '0 12px')};
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: ${surface};
  color: ${ink};
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);

  &:hover {
    background: ${surfaceHover};
    transform: translateY(-1px);
    box-shadow: 0 8px 8px -4px rgb(0 0 0 / 0.15), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px -2px rgb(0 0 0 / 0.1);
  }

  i.icon {
    margin: 0 !important;
    font-size: 1.1rem;
    display: block;
  }

  /* The inline camera-off glyph sits where a font icon would.
     Sized in em so it tracks the same font-size the font icons use, and scaled
     past 1em because it does not fill its box the way a glyph does: the camera
     body spans a little over half the 24-unit viewBox, where the mic glyph's
     ink fills most of its em box. At 1em the camera read noticeably smaller
     than the mic beside it.
     display:block so it does not pick up the inline baseline gap that would
     sit it a pixel high against the label. */
  svg {
    display: block;
    width: 1.4em;
    height: 1.4em;
  }

  .toolbar-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  }
`;
/**
 * Camera-off glyph: a camera with a slash through it, which is what Teams and
 * Zoom both use for this state.
 *
 * Inline rather than a Semantic <Icon> because this build of the icon font has
 * no slashed camera. It defines microphone + microphone.slash, but only video,
 * video.camera and video.play — no video.slash. The original code fell back to
 * `ban`, a no-entry circle, which reads as "blocked" or "broken" rather than
 * "switched off". ParticipantTile already carries an inline SVG for the same
 * reason.
 *
 * currentColor so it inherits the button's colour like the font icons do.
 */
const VideoOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false"
    data-testid="video-off-icon"
  >
    <path d="M10.66 5H14a2 2 0 0 1 2 2v2.34l1 1L22 7v10" />
    <path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

const RecordingButton = ({ recording, toggleRecording, icon = 'phone', label, active = true, onClick, title }) => {
    // Use provided onClick or fallback to toggleRecording
    const handleClick = onClick || toggleRecording;
  const hasLabel = Boolean(label);
  const cameraOff = icon === 'video' && !active;

    return (
        <div className='button-card'>
            <div className='video-cta'>
                <ToolbarButton
                    recording={recording}
                    active={active}
          hasLabel={hasLabel}
                    onClick={handleClick}
          aria-label={title || label}
                    title={title}
                >
          {icon && (cameraOff ? <VideoOffIcon /> : (
            <Icon
              name={
                icon === 'phone' ? (recording ? 'stop' : 'phone') :
                icon === 'video' ? 'video' :
                icon === 'microphone' ? (active ? 'microphone' : 'microphone slash') :
                'question'
              }
            />
          ))}
          {hasLabel && (
            <span className="toolbar-label">{label}</span>
          )}
                </ToolbarButton>
            </div>
        </div>
    );
};

export default RecordingButton;